// Type imports
import type { ColumnDef } from "@tanstack/react-table";

import { Button, FormCheckbox, IconBin, ScrollArea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Table, Tabs, TabsContent, TabsList, TabsTrigger, Text } from "@sentricmusic/ui";
// Value imports
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import useWindowDimensions from "@/newComponents/utils/useWindowDimensions";
import { useUIStateStore } from "@/store/uiStateStore";

import type { SongWriterRow } from "../components/AssignWritersCells";

import { AssignmentCell, AssignmentHeaderCell, getEditableSongIds, SongTitleCell } from "../components/AssignWritersCells";
import useMultiSongStore from "../store/multiSongStore";
import useSongWriterAssignmentStore from "../store/songWriterAssignmentStore";
import useWriterStore from "../store/writerStore";

export default function AssignWritersStep() {
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const { t } = useTranslation("album");
  const { isBelowMd } = useWindowDimensions();

  const { tracks } = useMultiSongStore();
  const { selectedWriters } = useWriterStore();
  const initializeMatrix = useSongWriterAssignmentStore(state => state.initializeMatrix);
  const toggleWriterForSong = useSongWriterAssignmentStore(state => state.toggleWriterForSong);
  const setWriterForSong = useSongWriterAssignmentStore(state => state.setWriterForSong);
  // Subscribe to assignments for statsBlock and song-by-song tab rendering only.
  // Callbacks that need assignments read imperatively via getState() to avoid
  // destabilizing the columns useMemo dependency chain.
  const assignments = useSongWriterAssignmentStore(state => state.assignments);

  // Track which auxiliary writer columns are unlocked (allows assigning to other songs)
  const [unlockedAuxWriters, setUnlockedAuxWriters] = useState<Set<string>>(() => new Set());

  // Toggle to hide recording/variant tracks from both tabs
  const [hideRecordings, setHideRecordings] = useState(true);

  // Memoize selected tracks to prevent new array references on every render
  const allSelectedTracks = useMemo(() => tracks.filter(track => track.isSelected), [tracks]);

  // Helper to check if a track is a recording or variant
  const isRecordingOrVariant = useCallback((track: typeof tracks[0]) => {
    const isRecording = track.isSuspectedDuplicate === true && track.reviewAction !== "dismiss";
    const isVariant = typeof track.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track.reviewAction !== "dismiss";
    return isRecording || isVariant;
  }, []);

  // Get tracks that can have writers assigned (excluding recordings and variants)
  const selectedTracks = useMemo(() => allSelectedTracks.filter(track => !isRecordingOrVariant(track)), [allSelectedTracks, isRecordingOrVariant]);

  // Use a ref to track initialization to prevent infinite loops
  const initializationKey = useRef<string>("");

  // State for active tab - default to bulk assignment table if only one writer
  const [activeTab, setActiveTab] = useState<string>(() => {
    return selectedWriters.length === 1 ? "table" : "song-by-song";
  });

  // Initialize the assignment matrix when component mounts or data changes
  useEffect(() => {
    if (selectedTracks.length > 0 && selectedWriters.length > 0) {
      // Create a key based on the current selection to avoid unnecessary re-initialization
      const currentKey = `${selectedTracks.map(t => t.id).join(",")}-${selectedWriters.map(w => w.writercode).join(",")}`;

      if (initializationKey.current !== currentKey) {
        initializeMatrix(selectedTracks, selectedWriters);
        initializationKey.current = currentKey;

        // Pre-populate auxiliary writer assignments
        selectedTracks.forEach((track) => {
          if (track.auxiliaryWriters && track.auxiliaryWriters.length > 0) {
            track.auxiliaryWriters.forEach((writerCode) => {
              // Only assign if this writer is in the selected writers list
              if (selectedWriters.some(w => w.writercode === writerCode)) {
                setWriterForSong(track.id, writerCode, true);
              }
            });
          }
        });
      }
    }
  }, [selectedTracks, selectedWriters, initializeMatrix, setWriterForSong]);

  // Format track duration from milliseconds to MM:SS
  const formatDuration = (ms: number) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Check if any selected tracks are recordings/variants (to conditionally show toggle)
  const hasRecordingsOrVariants = useMemo(
    () => allSelectedTracks.some(track => isRecordingOrVariant(track)),
    [allSelectedTracks, isRecordingOrVariant],
  );

  // Prepare data for the table - include all tracks but mark recordings/variants
  const tableData = useMemo<SongWriterRow[]>(() => {
    return allSelectedTracks.map((track) => {
      const isRecording = track.isSuspectedDuplicate === true && track.reviewAction !== "dismiss";
      const isVariant = typeof track.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track.reviewAction !== "dismiss";
      const isRecOrVar = isRecording || isVariant;

      let recordingLabel = "";
      if (isVariant) {
        recordingLabel = `Recording of "${track.suspectedAlbumTrackTitle}"`;
      }
      else if (isRecording) {
        recordingLabel = `Recording of "${track.suspectedWorkTitle}"`;
      }

      return {
        songId: track.id,
        songTitle: track.name,
        artistName: track.artists.map(artist => artist.name).join(", "),
        duration: formatDuration(track.duration_ms),
        isRecordingOrVariant: isRecOrVar,
        recordingLabel,
      };
    });
  }, [allSelectedTracks]);

  // Filtered table data — hides recording/variant rows when toggle is active
  const filteredTableData = useMemo(
    () => hideRecordings ? tableData.filter(row => !row.isRecordingOrVariant) : tableData,
    [tableData, hideRecordings],
  );

  // Create stable callback functions to prevent re-renders
  // Check if a writer is locked to a specific song (auxiliary writer)
  // Check if a writer is an auxiliary writer (attached to specific tracks)
  const isAuxiliaryWriter = useCallback((writerCode: string): boolean => {
    return selectedTracks.some(track => track.auxiliaryWriters?.includes(writerCode));
  }, [selectedTracks]);

  const isWriterLockedToSong = useCallback((songId: string, writerCode: string): boolean => {
    const track = selectedTracks.find(t => t.id === songId);
    // If this is an auxiliary writer locked to this specific song
    const isLockedToThisSong = Boolean(track?.auxiliaryWriters?.includes(writerCode));

    // If this is an auxiliary writer column, check if non-auxiliary songs are locked
    const isAuxWriter = isAuxiliaryWriter(writerCode);
    if (isAuxWriter && !isLockedToThisSong) {
      // If the auxiliary writer column is unlocked, allow assignment
      if (unlockedAuxWriters.has(writerCode)) {
        return false;
      }
      // Otherwise, lock all songs that DON'T have this writer as auxiliary
      return true;
    }

    return isLockedToThisSong;
  }, [selectedTracks, isAuxiliaryWriter, unlockedAuxWriters]);

  const handleToggleWriterForSong = useCallback((songId: string, writerCode: string) => {
    // Don't allow toggling if this is an auxiliary writer locked to this song
    if (isWriterLockedToSong(songId, writerCode)) {
      return;
    }

    toggleWriterForSong(songId, writerCode);
  }, [toggleWriterForSong, isWriterLockedToSong]);

  const handleAssignWriterToAllSongs = useCallback((writerCode: string, checked: boolean) => {
    // Read assignments imperatively from the store to avoid closing over reactive state
    const currentAssignments = useSongWriterAssignmentStore.getState().assignments;
    // Only assign to non-recording tracks
    selectedTracks.forEach((track) => {
      const currentValue = currentAssignments[track.id]?.[writerCode] || false;
      if (currentValue !== checked) {
        toggleWriterForSong(track.id, writerCode);
      }
    });
  }, [selectedTracks, toggleWriterForSong]);

  const handleAssignAllWritersToSong = useCallback((songId: string, checked: boolean) => {
    // Read assignments imperatively from the store to avoid closing over reactive state
    const currentAssignments = useSongWriterAssignmentStore.getState().assignments;
    // Only assign/unassign writers that aren't locked to this song
    selectedWriters.forEach((writer) => {
      const isLocked = isWriterLockedToSong(songId, writer.writercode);
      const isAssigned = currentAssignments[songId]?.[writer.writercode];

      // Only toggle if: not locked, and the current state differs from desired state
      if (!isLocked && isAssigned !== checked) {
        toggleWriterForSong(songId, writer.writercode);
      }
    });
  }, [selectedWriters, isWriterLockedToSong, toggleWriterForSong]);

  const handleToggleAuxWriterLock = useCallback((writerCode: string) => {
    setUnlockedAuxWriters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(writerCode)) {
        newSet.delete(writerCode);
      }
      else {
        newSet.add(writerCode);
      }
      return newSet;
    });
  }, []);

  // Define table columns dynamically based on selected writers
  const columns = useMemo<ColumnDef<SongWriterRow>[]>(() => {
    const baseColumns: ColumnDef<SongWriterRow>[] = [
      {
        id: "song_title",
        enableSorting: false,
        // header: "Song Title",
        header: t("album:song-title") ?? "Song Title",
        accessorKey: "songTitle",
        size: 220,
        minSize: 180,
        maxSize: 300,
        // Pin the song title column to the left with opaque background
        enablePinning: true,
        meta: { pinSide: "left", pinnedBackground: true },
        cell: ({ row }) => {
          // If this is a recording/variant, show it differently
          if (row.original.isRecordingOrVariant) {
            return (
              <div className="flex flex-col flex-1 gap-1 overflow-hidden opacity-60">
                <Text as="p" className="text-sm font-semibold truncate">
                  {row.original.songTitle}
                </Text>
                <Text as="p" className="text-xs truncate text-sentric-grey-text">
                  {row.original.recordingLabel}
                </Text>
              </div>
            );
          }

          // Calculate unlocked writers (writers that can be toggled for this song)
          const unlockedWriterCodes = selectedWriters
            .filter(writer => !isWriterLockedToSong(row.original.songId, writer.writercode))
            .map(writer => writer.writercode);

          return (
            <SongTitleCell
              songId={row.original.songId}
              songTitle={row.original.songTitle}
              unlockedWriterCodes={unlockedWriterCodes}
              onAssignAll={handleAssignAllWritersToSong}
            />
          );
        },
      },
    ];

    // Add columns for each selected writer
    const writerColumns: ColumnDef<SongWriterRow>[] = selectedWriters.map(writer => ({
      id: `writer_${writer.writercode}`,
      size: isBelowMd ? 100 : 180,
      minSize: isBelowMd ? 80 : 140,
      maxSize: 240,
      header: ({ table }) => {
        const editableSongIds = getEditableSongIds(table);
        return (
          <AssignmentHeaderCell
            writer={writer}
            editableSongIds={editableSongIds}
            isAuxWriter={isAuxiliaryWriter(writer.writercode)}
            isUnlocked={unlockedAuxWriters.has(writer.writercode)}
            onToggleAll={handleAssignWriterToAllSongs}
            onToggleLock={handleToggleAuxWriterLock}
          />
        );
      },
      cell: ({ row }) => {
        // For recordings/variants, show an empty disabled cell
        if (row.original.isRecordingOrVariant) {
          return (
            <div className="flex justify-center">
              <div className="w-full h-6 rounded-md sm:h-8 bg-sentric-mid-grey/30 opacity-40" />
            </div>
          );
        }

        return (
          <AssignmentCell
            songId={row.original.songId}
            songTitle={row.original.songTitle}
            writerCode={writer.writercode}
            writerName={writer.writername}
            isLocked={isWriterLockedToSong(row.original.songId, writer.writercode)}
            onToggle={handleToggleWriterForSong}
          />
        );
      },
    }));

    // Separate main writers from auxiliary writers
    const mainWriterColumns = writerColumns.filter((_, index) => {
      const writer = selectedWriters[index];
      return !isAuxiliaryWriter(writer.writercode);
    });

    const auxWriterColumns = writerColumns.filter((_, index) => {
      const writer = selectedWriters[index];
      return isAuxiliaryWriter(writer.writercode);
    });

    // Return the columns in the correct order: song_title, main writers, then auxiliary writers
    return [...baseColumns, ...mainWriterColumns, ...auxWriterColumns];
  // eslint-disable-next-line react-hooks/exhaustive-deps -- assignments intentionally omitted: cell components self-subscribe via Zustand selectors
  }, [
    isAuxiliaryWriter,
    isBelowMd,
    isWriterLockedToSong,
    unlockedAuxWriters,
    handleToggleAuxWriterLock,
    selectedWriters,
    handleAssignWriterToAllSongs,
    handleToggleWriterForSong,
    handleAssignAllWritersToSong,
    isTunecore,
    t,
  ]);

  // Calculate stats for the table header
  const statsBlock = useMemo(() => {
    const songsWithNoWriters = selectedTracks.filter((track) => {
      const songAssignments = assignments[track.id];
      // Check if song has no writers assigned
      return songAssignments == null || selectedWriters.every(writer => !songAssignments[writer.writercode]);
    }).length;

    return [
      // { title: "Total Songs", value: selectedTracks.length || 0, highlight: false },
      { title: t("album:total-songs") ?? "Total Songs", value: selectedTracks.length || 0, highlight: false },
      // { title: "Songs with no writers assigned", value: songsWithNoWriters, highlight: true },
      { title: t("album:songs-with-no-writers-assigned") ?? "Songs with no writers assigned", value: songsWithNoWriters, highlight: true },
    ];
  }, [selectedTracks, assignments, selectedWriters, t]);

  if (selectedTracks.length === 0) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className="text-gray-500">
          {/* No songs selected. Please go back to step 2 and select some songs. */}
          {t("album:no-songs-selected")}
        </Text>
      </div>
    );
  }

  if (selectedWriters.length === 0) {
    return (
      <div className="py-8 text-center">
        <Text as="p" className="text-gray-500">
          {/* No writers selected. Please go back to step 3 and select some writers. */}
          {t("album:no-writers-selected")}
        </Text>
      </div>
    );
  }

  return (

    <div className="w-full min-w-0">
      {/* Toggle to hide recording/variant tracks */}
      {hasRecordingsOrVariants && (
        <div className="flex items-center mb-4">
          <FormCheckbox
            id="hide-recordings-toggle"
            checked={hideRecordings}
            onCheckedChange={checked => setHideRecordings(Boolean(checked))}
            label={t("album:hide-recordings-and-variants", "Hide recordings & variants")}
          />
        </div>
      )}
      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="song-by-song" id="albumAssignWritersIndividualStep">
            {isBelowMd === true ? (t("album:individual") ?? "Individual") : (t("album:individual-song-assignment") ?? "Individual Song Assignment")}
          </TabsTrigger>

          <TabsTrigger value="table" id="albumAssignWritersBulkStep">
            {isBelowMd === true ? (t("album:bulk") ?? "Bulk") : (t("album:bulk-assignment-table") ?? "Bulk Assignment Table")}
          </TabsTrigger>
        </TabsList>

        {/* Table View Tab */}
        <TabsContent value="table" className="w-full mt-6 space-y-6">
          <Table
            enableColumnResizing={false}
            canExpandToDialog={true}
            hasFilters={false}
            data={filteredTableData}
            columns={columns}
            enableColumnPinning={true}
            paginationComponent={false}
            pagination={{ pageIndex: 0, pageSize: tracks.length || 1000 }}
            statsBlock={statsBlock}
            height="calc(100vh - 400px)"
          />
        </TabsContent>
        {" "}
        {/* Individual Song Assignment 2.0 Tab */}
        <TabsContent value="song-by-song" className="mt-6 space-y-6">
          <ScrollArea className={`h-[calc(100vh-400px)] w-full rounded-lg shadow-md ${isTunecore ? "text-white p-4 rounded-lg bg-[#282828]" : "bg-sentric-light-grey p-4 rounded-lg text-old-sentric-dark-gray"}`}>
            <div className="space-y-4">
              {allSelectedTracks.map((track) => {
                // Check if this is a recording/variant
                const isRecording = track.isSuspectedDuplicate === true && track.reviewAction !== "dismiss";
                const isVariant = typeof track.suspectedAlbumTrackId === "string" && track.suspectedAlbumTrackId.length > 0 && track.reviewAction !== "dismiss";
                const isRecOrVar = isRecording || isVariant;

                // Hide recording/variant tracks when toggle is active
                if (hideRecordings && isRecOrVar) {
                  return null;
                }

                // For recordings/variants, show a simplified display
                if (isRecOrVar) {
                  const recordingLabel = isVariant
                    ? `Recording of "${track.suspectedAlbumTrackTitle}"`
                    : `Recording of "${track.suspectedWorkTitle}"`;

                  return (
                    <div key={track.id} className={`p-4 rounded-lg shadow-sm opacity-60 ${isTunecore ? "bg-tunecore-grey-element" : "bg-white border border-sentric-mid-grey"}`}>
                      <div className="space-y-2">
                        <Text as="subtitle">{track.name}</Text>
                        <Text as="p" className="text-sm text-sentric-grey-text">
                          {recordingLabel}
                        </Text>

                      </div>
                    </div>
                  );
                }

                const songAssignments = assignments[track.id];

                // Filter out locked auxiliary writers that aren't assigned to this song
                const availableWritersForThisSong = selectedWriters.filter((writer) => {
                  const isLocked = isWriterLockedToSong(track.id, writer.writercode);
                  const isAssigned = songAssignments?.[writer.writercode];
                  // Include if not locked, OR if locked and already assigned
                  return !isLocked || isAssigned;
                });

                // Always read directly from assignments matrix, not from songBysongSelections
                const assignedWriters = availableWritersForThisSong.filter(writer =>
                  songAssignments?.[writer.writercode],
                );
                const unassignedWriters = availableWritersForThisSong.filter(writer =>
                  !songAssignments?.[writer.writercode],
                );
                const allWritersAssigned = availableWritersForThisSong.length > 0
                  && availableWritersForThisSong.every(writer => songAssignments?.[writer.writercode]);

                return (
                  <div key={track.id} className={`p-4 rounded-lg shadow-sm transition-colors ${isTunecore ? "bg-tunecore-grey-element" : "bg-white border border-sentric-mid-grey"}`}>
                    <div className="space-y-4">
                      {/* Song info */}
                      <div>
                        <Text as="subtitle">{track.name}</Text>
                        <div className="flex items-center gap-4">
                          <Text as="p">
                            {/* Artist: */}
                            {t("album:artist") ?? "Artist"}
                            :
                            {" "}
                            {track.artists.map(artist => artist.name).join(", ")}
                          </Text>
                          <Text as="p">
                            {/* Duration: */}
                            {t("album:duration") ?? "Duration"}
                            :
                            {" "}
                            {formatDuration(track.duration_ms)}
                          </Text>
                        </div>
                      </div>

                      {/* Select All Checkbox */}
                      <div className="flex items-center gap-2">
                        <FormCheckbox
                          id={`select-all-${track.id}`}
                          checked={allWritersAssigned}
                          onChange={e => handleAssignAllWritersToSong(track.id, e.target.checked)}
                          // label="Select all writers"
                          label={t("album:select-all-writers") ?? "Select all writers"}
                          className="cursor-pointer"
                        />
                      </div>

                      {/* Writer Dropdown */}
                      <div className="space-y-2">
                        <Text as="p" className="text-sm font-medium">
                          {t("album:add-writer") ?? "Add writer"}
                          :
                        </Text>
                        <Select
                          disabled={allWritersAssigned || unassignedWriters.length === 0}
                          value=""
                          onValueChange={(writerCode) => {
                            if (writerCode) {
                              handleToggleWriterForSong(track.id, writerCode);
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder={
                              allWritersAssigned
                                ? (t("album:all-writers-selected") ?? "All writers selected")
                                : unassignedWriters.length === 0
                                  ? (t("album:no-writers-available") ?? "No writers available")
                                  : (t("album:choose-writer-to-add") ?? "Choose a writer to add...")
                            }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="-1" disabled>
                              {t("album:choose-writer-to-add") ?? "Choose a writer to add..."}
                            </SelectItem>
                            {unassignedWriters
                              .filter(writer => writer.writercode != null && writer.writercode.trim() !== "")
                              .map(writer => (
                                <SelectItem key={writer.writercode} value={writer.writercode}>
                                  {writer.writername}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Assigned writers list */}
                      <div className="space-y-2">
                        <Text as="p" className="text-sm font-medium">
                          {t("album:assigned-writers")}
                          {" "}
                          (
                          {assignedWriters.length}
                          )
                        </Text>
                        <div className="space-y-2 ">
                          {assignedWriters.length === 0 ? (
                            <Text as="p" className="text-sm italic text-gray-500">
                              {/* No writers assigned */}
                              {t("album:no-writers-assigned")}
                            </Text>
                          ) : (
                            assignedWriters.map(writer => (
                              <div
                                key={writer.writercode}
                                className={`flex items-center justify-between p-2 rounded-md ${isTunecore ? "bg-[#282828]" : "bg-sentric-light-grey"}`}
                              >
                                <Text as="p" className="text-sm font-medium">
                                  {writer.writername}
                                </Text>
                                <Button
                                  icon={<IconBin className={`w-5 h-5 ${isTunecore ? "stroke-white group-hover:stroke-tunecore-green" : "stroke-black"}`} />}
                                  variant="link"
                                  onClick={() => handleToggleWriterForSong(track.id, writer.writercode)}
                                  className="w-6 h-6 p-0 group hover:scale-110"
                                />
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}
